import { useState, useEffect } from "react";
import { StriimInputField } from "@striim/striim-ui";
import { Grid } from "@mui/material";

import growl from "app/components/common/growl";
import api from "core/api/api";
import EmbeddingGeneratorDetailsModal from "../../modules/apps/pages/manage-striim/embedding-generator/embedding-generator-details-modal";
import { getEmptyDetails } from "../../modules/apps/pages/manage-striim/embedding-generator/embedding-generator-service";

// Constant for the "New Embedding Generator" option
const NEW_EMBEDDING_GENERATOR_IDENTIFIER = "NEW_EMBEDDING_GENERATOR";

const EmbeddingGeneratorSelectorDirect = ({ value, ...props }) => {
    const [selectOptions, setSelectOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [initialValues, setInitialValues] = useState(getEmptyDetails());
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);

    const getEmbeddingGenerators = async () => {
        let options = [];
        try {
            const embeddingGenerators = await api.getEmbeddingGenerators();
            // Process the embedding generators based on the format returned by the API
            if (Array.isArray(embeddingGenerators)) {
                options = embeddingGenerators.map(eg => {
                    // Check if the embedding generator has the expected properties
                    if (eg && eg.nsName && eg.name) {
                        return {
                            label: `${eg.nsName}.${eg.name}`,
                            value: `${eg.nsName}.${eg.name}`
                        };
                    } else if (eg && eg.id) {
                        // If the embedding generator has an ID but not nsName/name, try to extract them from the ID
                        const parts = eg.id.split('.');
                        if (parts.length >= 3) {
                            const nsName = parts[0];
                            const name = parts[2];
                            return {
                                label: `${nsName}.${name}`,
                                value: `${nsName}.${name}`
                            };
                        }
                    }
                    // If we can't extract the necessary information, log a warning and return null
                    console.warn("Embedding generator with unexpected format:", eg);
                    return null;
                }).filter(Boolean); // Filter out null values
            } else {
                console.warn("Embedding generators is not an array:", embeddingGenerators);
            }
        } catch (error) {
            console.error("Error fetching embedding generators:", error);
            growl.error("Error fetching embedding generators: " + error);
        } finally {
            // Add the "New Embedding Generator" option at the top
            options.unshift({ label: "+ New Embedding Generator", value: NEW_EMBEDDING_GENERATOR_IDENTIFIER });
            setSelectOptions(options);
        }
    };

    useEffect(() => {
        getEmbeddingGenerators();
    }, []);

    useEffect(() => {
        setSelectedOption(value ? { label: value, value } : "");
    }, [value]);

    const handleChange = currentSelection => {
        const value = currentSelection?.value;

        if (value === NEW_EMBEDDING_GENERATOR_IDENTIFIER) {
            // Reset the form with empty values
            setInitialValues(getEmptyDetails());
            setShowModal(true);
        } else {
            props.onChange(value || "");
            setSelectedOption(currentSelection || "");
        }
    };

    const cancelCreatingEG = () => {
        setShowModal(false);
        // If we had a previous selection, restore it
        if (selectedOption && selectedOption !== NEW_EMBEDDING_GENERATOR_IDENTIFIER) {
            props.onChange(selectedOption.value);
            setSelectedOption(selectedOption);
        }
    };

    const saveEmbeddingGenerator = async formValues => {
        try {
            setIsSaveDisabled(true);
            // Save the embedding generator
            await formValues.save();

            // Refresh the list of embedding generators
            await getEmbeddingGenerators();

            // Set the newly created embedding generator as selected
            const newValue = `${formValues.get("nsName")}.${formValues.get("name")}`;
            props.onChange(newValue);
            setSelectedOption({ label: newValue, value: newValue });

            // Close the modal
            setShowModal(false);

            // Show success message
            growl.success("Vector Embeddings Generator saved successfully");
        } catch (error) {
            console.error("Error saving embedding generator:", error);
            growl.error("Error saving embedding generator: " + error);
        } finally {
            setIsSaveDisabled(false);
        }
    };

    return (
        <Grid container>
            <StriimInputField
                name={props.name}
                label={props.label || "Embedding Generator "}
                value={selectedOption}
                required={props.required}
                select
                id="data-test-id-connection-profile-select"
                SelectProps={{
                    options: selectOptions,
                    onMenuOpen: getEmbeddingGenerators,
                    // ...(useMenuPortalTarget && { menuPortalTarget: document.body }),
                    components: {
                        IndicatorSeparator: () => null
                    }
                }}
                onChange={value => {
                    handleChange(value);
                }}
                placeholder={"Select an embedding generator"}
                disabled={!props.enabled}
                error={props.error}
                helperText={props.error ? props.errorText : ""}
            />

            <EmbeddingGeneratorDetailsModal
                open={showModal}
                initialValues={initialValues}
                onSave={saveEmbeddingGenerator}
                onCancel={cancelCreatingEG}
            />
        </Grid>
    );
};

export default EmbeddingGeneratorSelectorDirect;
